/**
 * FolderRail — the mail navigation rail (plan §1.2, §11.1).
 *
 * Renders the lane's system views (Inbox / Starred / Snoozed / Sent / Drafts),
 * two disclosure groups (Categories / More) and the TAGS section, from the
 * single model `GET /api/mail/folders` returns.
 *
 * ## `count: null` renders NO badge
 *
 * A zero is a folder that was counted and is empty. A null is a folder this
 * backend does not count — Drafts and Scheduled, which have no data source in
 * this repo (there is no server-side draft store and scheduled send does not
 * exist). Rendering `0` where nothing should appear is a visible regression, so
 * the badge is gated on `count != null`, never on truthiness. `count ?? 0` and
 * `count || 0` are both wrong here.
 *
 * ## Tags (#646)
 *
 * The section lists the org's tags as they apply to THIS lane — colour, name
 * and a count of conversations the viewer can see (`model.labels`, a wire name
 * kept from before the feature; ADR-002). Two rules:
 *
 * - **A tag is a second filter, not a view.** It has its own selection
 *   (`activeTag` / `onSelectTag`), so picking one narrows the current folder
 *   instead of replacing it. Clicking the active tag again clears it.
 * - **Controls follow permission.** "+" / *Create tag* show only when the lane
 *   may tag (`canTag` — a unified inbox's viewers may not); *Manage tags* only
 *   for admins (`canManageTags`), because Admin → Tags is admin-routed.
 *
 * ## Layout
 *
 * The rail is a flex child of an already-bounded column. The scroll region is
 * therefore `flex-1 min-h-0` — `h-full` is only correct for the sole child of a
 * column that is itself bounded, and `position: fixed` is never correct here
 * (it escapes the shell's scroll container and the RuntimeShell rail's z-order).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    ChevronDown,
    ChevronRight,
    Clock,
    FileText,
    Inbox,
    Plus,
    Send,
    Settings,
    Star,
    Tag,
} from 'lucide-react';

import { useQueryClient } from '@tanstack/react-query';

import { useMailFolders } from '../../hooks/useMailFolders';
import { mailFolderKeys } from '../../hooks/mailFolderKeys';
import MailTagPicker from './MailTagPicker';

// Locked in plan §11.1. The app has no custom neutral ramp — `stone-*` is the
// repo's neutral (318 files) — so only the accent and hover values are literal.
const ROW = 'flex items-center gap-[9px] h-[34px] px-[10px] rounded-[9px] text-[13px] cursor-pointer';
const ACTIVE = 'bg-[#EFEAFE] text-[#3B0DBE] font-semibold';
const IDLE = 'text-stone-700 hover:bg-[#F2F0EC]';

const SECTION_HEADER = 'text-[10.5px] font-semibold tracking-wide';
const SECTION_HEADER_COLOR = '#A29B95';

/** The backend sends icon NAMES, not components — this is the only mapping. */
const ICONS = {
    inbox: Inbox,
    star: Star,
    clock: Clock,
    send: Send,
    'file-text': FileText,
};

/**
 * The count badge, or nothing.
 *
 * `count == null` covers both null and undefined and is the whole point of this
 * component: it is the one place the null-vs-zero rule is enforced.
 */
function CountBadge({ count }) {
    if (count == null) return null;
    return (
        <span className="ml-auto text-[11px] tabular-nums" data-testid="folder-count">
            {count}
        </span>
    );
}

/** A 16px slot so group children line up with the system rows' icons. */
function IconSlot({ name }) {
    const Icon = name ? ICONS[name] : null;
    if (!Icon) return <span className="w-4 flex-shrink-0" aria-hidden="true" />;
    return <Icon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />;
}

function FolderRow({ item, active, onSelect }) {
    return (
        <button
            type="button"
            onClick={() => onSelect?.(item.id)}
            aria-current={active ? 'page' : undefined}
            data-testid={`folder-row-${item.id}`}
            className={`w-full text-left ${ROW} ${active ? ACTIVE : IDLE}`}
        >
            <IconSlot name={item.icon} />
            <span className="truncate">{item.label}</span>
            <CountBadge count={item.count} />
        </button>
    );
}

/** One tag: its shared colour, its name, its lane count. */
function TagRow({ tag, active, onSelect }) {
    return (
        <button
            type="button"
            onClick={() => onSelect?.(active ? null : tag.id)}
            aria-pressed={active}
            title={active ? `Clear the ${tag.name} filter` : `Show mail tagged ${tag.name}`}
            data-testid={`folder-tag-${tag.id}`}
            className={`w-full text-left ${ROW} ${active ? ACTIVE : IDLE}`}
        >
            {tag.color ? (
                <span
                    className="w-[9px] h-[9px] mx-[3.5px] rounded-full flex-shrink-0"
                    style={{ backgroundColor: tag.color }}
                    data-testid="folder-tag-color"
                    aria-hidden="true"
                />
            ) : (
                <Tag className="w-4 h-4 flex-shrink-0 text-stone-400" aria-hidden="true" />
            )}
            <span className="truncate">{tag.name}</span>
            <CountBadge count={tag.count} />
        </button>
    );
}

/** Which group, if any, holds a given view id. */
function groupIdContaining(groups, viewId) {
    if (!viewId) return null;
    const match = (groups || []).find(group =>
        (group.children || []).some(child => child.id === viewId)
    );
    return match ? match.id : null;
}

export default function FolderRail({
    model,
    activeView = 'inbox',
    onSelectView,
    /** The selected tag_id, or null. A second filter, composed with the folder. */
    activeTag = null,
    /** `(tagId | null) => void` — null clears the tag filter. */
    onSelectTag,
    /** Whether this lane may create/apply tags (a unified inbox's viewers may not). */
    canTag = false,
    /** Admin → Tags is admin-routed; the link shows only for admins. */
    canManageTags = false,
    onManageTags,
    /** `({ add }) => void` after the "+" picker resolves a tag (created or existing). */
    onTagCreated,
    className = '',
}) {
    const system = model?.system || [];
    const groups = useMemo(() => model?.groups || [], [model]);
    const tags = model?.labels || [];
    const [creating, setCreating] = useState(false);
    const addTagRef = useRef(null);

    // Collapsed by default — the rail's job is the five system views, and both
    // groups are overflow. A group holding the ACTIVE view opens regardless,
    // because a rail that hides where you currently are is a rail that lies.
    const [openGroups, setOpenGroups] = useState(() => {
        const owner = groupIdContaining(groups, activeView);
        return owner ? { [owner]: true } : {};
    });

    useEffect(() => {
        const owner = groupIdContaining(groups, activeView);
        if (!owner) return;
        setOpenGroups(prev => (prev[owner] ? prev : { ...prev, [owner]: true }));
    }, [groups, activeView]);

    const toggleGroup = useCallback(id => {
        setOpenGroups(prev => ({ ...prev, [id]: !prev[id] }));
    }, []);

    const closeCreator = useCallback(() => setCreating(false), []);

    return (
        <nav
            aria-label="Mail folders"
            data-testid="folder-rail"
            className={`w-[214px] flex-shrink-0 bg-[#FBFAF9] p-[10px_10px_24px] flex flex-col gap-[2px] overflow-hidden ${className}`}
        >
            {/* `flex-1 min-h-0`, never `h-full`: the rail is one flex child of a
                bounded column, so the scroll region has to be allowed to shrink
                below its content height or the whole column grows instead. */}
            <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-[2px]">
                {system.map(item => (
                    <FolderRow
                        key={item.id}
                        item={item}
                        active={item.id === activeView}
                        onSelect={onSelectView}
                    />
                ))}

                {groups.map(group => {
                    const isOpen = Boolean(openGroups[group.id]);
                    const Chevron = isOpen ? ChevronDown : ChevronRight;
                    return (
                        <div key={group.id} className="flex flex-col gap-[2px]">
                            <button
                                type="button"
                                onClick={() => toggleGroup(group.id)}
                                aria-expanded={isOpen}
                                data-testid={`folder-group-${group.id}`}
                                className={`w-full text-left ${ROW} ${IDLE}`}
                            >
                                {/* 11px chevron gutter (§11.1). */}
                                <span
                                    className="w-[11px] flex-shrink-0 flex items-center"
                                    aria-hidden="true"
                                >
                                    <Chevron className="w-[11px] h-[11px]" />
                                </span>
                                <span className="truncate">{group.label}</span>
                            </button>

                            {isOpen &&
                                (group.children || []).map(child => (
                                    <FolderRow
                                        key={child.id}
                                        item={child}
                                        active={child.id === activeView}
                                        onSelect={onSelectView}
                                    />
                                ))}
                        </div>
                    );
                })}

                {/* TAGS (#646) — the org vocabulary as it applies to this lane. */}
                <div className="relative flex items-center justify-between px-[10px] pt-[14px] pb-[4px]">
                    <span
                        className={SECTION_HEADER}
                        style={{ color: SECTION_HEADER_COLOR }}
                        data-testid="folder-tags-header"
                    >
                        TAGS
                    </span>
                    {canTag && (
                        <button
                            type="button"
                            title="Create tag"
                            aria-label="Create tag"
                            aria-expanded={creating}
                            data-testid="folder-tags-add"
                            ref={addTagRef}
                            onClick={() => setCreating(open => !open)}
                            className="text-stone-500 hover:text-stone-800"
                        >
                            <Plus className="w-3.5 h-3.5" aria-hidden="true" />
                        </button>
                    )}
                </div>

                {creating && canTag && (
                    <div className="px-[2px] pb-[6px]">
                        <MailTagPicker
                            createOnly
                            heading="Create tag"
                            testId="folder-tags-creator"
                            className="w-full"
                            anchorRef={addTagRef}
                            onClose={closeCreator}
                            onChange={({ add }) => {
                                onTagCreated?.({ add });
                                setCreating(false);
                            }}
                        />
                    </div>
                )}

                {tags.map(tag => (
                    <TagRow
                        key={tag.id}
                        tag={tag}
                        active={tag.id === activeTag}
                        onSelect={onSelectTag}
                    />
                ))}

                {canManageTags && onManageTags && (
                    <button
                        type="button"
                        onClick={() => onManageTags()}
                        data-testid="folder-tags-manage"
                        className={`w-full text-left ${ROW} ${IDLE}`}
                    >
                        <Settings className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                        <span className="truncate">Manage tags</span>
                    </button>
                )}
                {canTag && tags.length === 0 && !creating && (
                    <button
                        type="button"
                        onClick={() => setCreating(true)}
                        data-testid="folder-tags-create"
                        className={`w-full text-left ${ROW} ${IDLE}`}
                    >
                        <Plus className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                        <span className="truncate">Create tag</span>
                    </button>
                )}
            </div>
        </nav>
    );
}

/**
 * The rail, wired to its own data.
 *
 * Kept separate from the default export so `FolderRail` stays renderable with a
 * plain object in a test — a rail that can only be rendered inside a
 * QueryClientProvider and an AuthProvider is a rail whose null-vs-zero rule
 * stops being checked.
 *
 * @param {string|null} inboxId  Unified inbox id, or null for the personal lane.
 */
export function ConnectedFolderRail({ inboxId = null, ...rest }) {
    const { data } = useMailFolders(inboxId);
    const queryClient = useQueryClient();
    // A tag created from "+" is a `Mail` tag, which every lane's rail lists at
    // zero — refetch them all rather than waiting out the 30s staleTime.
    const handleTagCreated = useCallback(
        () => queryClient.invalidateQueries({ queryKey: mailFolderKeys.all }),
        [queryClient],
    );
    return <FolderRail model={data} onTagCreated={handleTagCreated} {...rest} />;
}
